import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { supabase } from '../lib/supabase';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { cn } from '../lib/utils';
import toast from 'react-hot-toast';
import { format, subMinutes, addMinutes } from 'date-fns';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const { LuCamera, LuAlertCircle, LuCheckCircle2, LuRefreshCw, LuQrCode, LuPlus, LuLoader2 } = LuIcons;

export default function Scanner() {
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef(null);
  const scannerElementId = "qr-reader";
  const [scannedStudent, setScannedStudent] = useState(null);
  const [enrolledSubjects, setEnrolledSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Custom Date and Time states for attendance
  const [scanDate, setScanDate] = useState('');
  const [scanTime, setScanTime] = useState('');

  useEffect(() => {
    Html5Qrcode.getCameras().then(devices => {
      if (devices && devices.length) {
        setCameras(devices);
        const backCamera = devices.find(d => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('environment'));
        setSelectedCamera(backCamera ? backCamera.id : devices[0].id);
      }
    }).catch(err => {
      console.error("Camera access error", err);
      toast.error('Camera access denied');
    });
    return () => stopScanning();
  }, []);

  const startScanning = () => {
    if (!selectedCamera) return;
    setIsScanning(true);
    scannerRef.current = new Html5Qrcode(scannerElementId);
    scannerRef.current.start(
      selectedCamera,
      { fps: 15, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
      onScanSuccess,
      onScanFailure
    ).catch(err => {
      setIsScanning(false);
      toast.error('Failed to start camera');
    });
  };

  const stopScanning = () => {
    if (scannerRef.current && scannerRef.current.isScanning) {
      scannerRef.current.stop().then(() => {
        scannerRef.current.clear();
        setIsScanning(false);
      });
    } else {
      setIsScanning(false);
    }
  };

  const onScanSuccess = async (decodedText) => {
    if (scannerRef.current && scannerRef.current.isScanning) {
      scannerRef.current.pause();
    }
    setIsProcessing(true);

    try {
      let studentName = null;
      let studentIdCode = null;

      // Detect Google Form URL format
      if (decodedText.includes('entry.1777552258=')) {
        try {
          const url = new URL(decodedText);
          studentName = url.searchParams.get('entry.1777552258');
        } catch (e) {
          studentName = decodedText.split('entry.1777552258=')[1]?.split('&')[0];
        }
      } else if (decodedText.includes('tutordash.agency/')) {
        // New QR format: www.tutordash.agency/{student_id_code}
        const match = decodedText.match(/tutordash\.agency\/([^/?#\s]+)/);
        studentIdCode = match ? decodeURIComponent(match[1]) : null;
      } else {
        studentIdCode = decodedText;
      }

      let student = null, error = null;

      if (studentName) {
        // Search by name (case-insensitive) — goes through normal RLS
        const decodedName = decodeURIComponent(studentName).replace(/\+/g, ' ');
        const res = await supabase
          .from('students')
          .select('*, student_subjects(subject_id, subjects(id, name))')
          .ilike('full_name', decodedName)
          .maybeSingle();
        student = res.data; error = res.error;
      } else if (studentIdCode) {
        // Use SECURITY DEFINER RPC so teachers can resolve ANY student
        // by QR code regardless of their teacher assignment (RLS bypass)
        const res = await supabase.rpc('find_student_for_scanner', { p_id_code: studentIdCode });
        student = res.data; error = res.error;
      }

      if (error || !student) {
        toast.error(`Student not found: ${studentName ? decodeURIComponent(studentName) : decodedText}`);
        resumeScanning();
        return;
      }

      const subjects = student.student_subjects?.map(ss => ss.subjects) || [];
      
      if (subjects.length === 0) {
        toast.error(`${student.full_name} has no active enrollments.`);
        resumeScanning();
        return;
      }

      setScannedStudent(student);
      setEnrolledSubjects(subjects);
      setSelectedSubject(subjects.length === 1 ? subjects[0].id : '');
      
      // Default to current date and time
      const now = new Date();
      setScanDate(format(now, 'yyyy-MM-dd'));
      setScanTime(format(now, 'HH:mm'));
      
      setIsModalOpen(true);
    } catch (error) {
      console.error(error);
      toast.error('Verification error');
      resumeScanning();
    } finally {
      setIsProcessing(false);
    }
  };

  const onScanFailure = () => {};

  const resumeScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.resume();
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setScannedStudent(null);
    resumeScanning();
  };

  const handleConfirmAttendance = async () => {
    if (!selectedSubject) return;
    
    const loadToast = toast.loading("Recording attendance...");
    try {
      const selectedTimestamp = new Date(`${scanDate}T${scanTime}`);
      
      // Prevent double scans within 60 minutes of the selected time
      const windowStart = subMinutes(selectedTimestamp, 60).toISOString();
      const windowEnd = addMinutes(selectedTimestamp, 60).toISOString();

      const { data: recentScan } = await supabase
        .from('attendance')
        .select('timestamp')
        .eq('student_id', scannedStudent.id)
        .eq('subject_id', selectedSubject)
        .gte('timestamp', windowStart)
        .lte('timestamp', windowEnd)
        .limit(1);

      if (recentScan && recentScan.length > 0) {
        toast.error('Duplicate scan blocked (60m window)', { id: loadToast });
        handleModalClose();
        return;
      }

      const { error } = await supabase.from('attendance').insert([{
        student_id: scannedStudent.id,
        subject_id: selectedSubject,
        timestamp: selectedTimestamp.toISOString(),
        status: 'present',
        duration_hours: 1
      }]);

      if (error) throw error;
      
      toast.success(`${scannedStudent.full_name} marked present!`, { id: loadToast });
      handleModalClose();
    } catch (error) {
      toast.error('Failed to record attendance', { id: loadToast });
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-10">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Attendance Scanner</h1>
        <p className="text-slate-500">Supports both Student IDs and Form URLs</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
        <div className="md:col-span-3">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden">
            {isProcessing && (
              <div className="absolute inset-0 z-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <SafeIcon icon={LuLoader2} className="animate-spin text-primary-600" size={32} />
                  <p className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-widest">Identifying Student...</p>
                </div>
              </div>
            )}
            
            <div className="mb-6 space-y-4">
              <select 
                className="w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 px-4 py-3 font-semibold outline-none focus:ring-2 focus:ring-primary-500" 
                value={selectedCamera} 
                onChange={(e) => setSelectedCamera(e.target.value)} 
                disabled={isScanning}
              >
                {cameras.map(camera => (
                  <option key={camera.id} value={camera.id}>{camera.label || `Camera ${camera.id}`}</option>
                ))}
              </select>
              
              <Button 
                onClick={isScanning ? stopScanning : startScanning} 
                variant={isScanning ? 'danger' : 'primary'} 
                className="w-full py-4 text-lg font-bold rounded-2xl shadow-lg"
              >
                {isScanning ? 'Stop Scanner' : 'Start Camera'}
              </Button>
            </div>

            <div className="relative rounded-3xl overflow-hidden bg-slate-900 aspect-square border-4 border-slate-100 dark:border-slate-800">
              {!isScanning && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-slate-400 bg-slate-900/80 backdrop-blur-sm">
                  <div className="bg-slate-800 p-6 rounded-full mb-4">
                    <SafeIcon icon={LuQrCode} size={48} className="opacity-50" />
                  </div>
                  <p className="font-bold uppercase tracking-widest text-xs">Waiting for camera activation</p>
                </div>
              )}
              <div id={scannerElementId} className="w-full h-full object-cover"></div>
            </div>
          </div>
        </div>

        <div className="md:col-span-2 space-y-6">
          <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
            <h3 className="font-bold mb-6 flex items-center gap-2 text-slate-900 dark:text-white">
              <div className="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-500 rounded-xl">
                <SafeIcon icon={LuAlertCircle} size={20} />
              </div>
              Scanner Intelligence
            </h3>
            <ul className="space-y-6">
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 flex items-center justify-center text-xs font-bold shrink-0">1</div>
                <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">Point the camera at a Student ID QR or the Google Form response URL.</p>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 flex items-center justify-center text-xs font-bold shrink-0">2</div>
                <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">The system automatically extracts the student identity and retrieves enrollments.</p>
              </li>
              <li className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 flex items-center justify-center text-xs font-bold shrink-0">3</div>
                <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">Adjust the date and time optionally, then confirm the subject to log the record.</p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={handleModalClose} title="Attendance Verification" className="max-w-md rounded-[2.5rem]">
        {scannedStudent && (
          <div className="space-y-6">
            <div className="bg-primary-600 text-white p-8 rounded-[2rem] text-center shadow-xl shadow-primary-500/20">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 border-4 border-white/30">
                <span className="text-xl font-black">{scannedStudent.full_name?.charAt(0)}</span>
              </div>
              <h4 className="text-xl font-bold tracking-tight">{scannedStudent.full_name}</h4>
              <p className="text-xs font-mono opacity-70 mt-1">{scannedStudent.student_id_code}</p>
            </div>

            <div className="space-y-4">
              <label className="text-[11px] font-bold uppercase text-slate-400 tracking-widest px-2">Select Active Subject</label>
              <div className="grid grid-cols-1 gap-3">
                {enrolledSubjects.map(sub => (
                  <button 
                    key={sub.id} 
                    onClick={() => setSelectedSubject(sub.id)} 
                    className={cn(
                      "w-full px-5 py-4 rounded-2xl border-2 text-left transition-all group",
                      selectedSubject === sub.id 
                        ? "border-primary-600 bg-primary-50 dark:bg-primary-900/20" 
                        : "border-slate-100 dark:border-slate-800 hover:border-slate-200"
                    )}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className={cn(
                          "font-bold uppercase tracking-tight",
                          selectedSubject === sub.id ? "text-primary-700 dark:text-primary-400" : "text-slate-900 dark:text-white"
                        )}>{sub.name}</span>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="primary" className="text-[8px]">{sub.level}</Badge>
                        </div>
                      </div>
                      {selectedSubject === sub.id && (
                        <div className="bg-primary-600 text-white p-1.5 rounded-lg">
                          <SafeIcon icon={LuCheckCircle2} size={16} />
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2">
              <Input 
                type="date" 
                label="Attendance Date" 
                value={scanDate} 
                onChange={(e) => setScanDate(e.target.value)} 
                required 
              />
              <Input 
                type="time" 
                label="Attendance Time" 
                value={scanTime} 
                onChange={(e) => setScanTime(e.target.value)} 
                required 
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={handleModalClose} className="flex-1 rounded-xl py-4">Cancel</Button>
              <Button 
                onClick={handleConfirmAttendance} 
                disabled={!selectedSubject || !scanDate || !scanTime} 
                className="flex-1 rounded-xl py-4 shadow-lg font-bold"
              >
                Log Presence
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}